import { useCallback, useEffect, useState } from "react";

export type Locale = "en" | "es";

const STORAGE_KEY = "emrkt.locale";

function readInitial(): Locale {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "es") return stored;
  const nav = window.navigator?.language ?? "en";
  return nav.toLowerCase().startsWith("es") ? "es" : "en";
}

function apply(locale: Locale) {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

// Cheap cross-component sync via a window event.
const EVT = "emrkt:locale-change";

export function useLocale() {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const l = readInitial();
    apply(l);
    return l;
  });

  useEffect(() => {
    const onChange = (e: Event) => {
      const next = (e as CustomEvent<Locale>).detail;
      if (next === "en" || next === "es") setLocaleState(next);
    };
    window.addEventListener(EVT, onChange as EventListener);
    return () => window.removeEventListener(EVT, onChange as EventListener);
  }, []);

  const setLocale = useCallback((next: Locale) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    apply(next);
    window.dispatchEvent(new CustomEvent(EVT, { detail: next }));
    setLocaleState(next);
  }, []);

  return { locale, setLocale };
}

/** Pick between an EN and ES string given a locale. Falls back to EN. */
export function pick<T>(locale: Locale, en: T, es: T | undefined): T {
  if (locale === "es" && es !== undefined) return es;
  return en;
}